/**
 * Claim state for the dashboard: the two reward calculations, the claim history,
 * and the three write flows (claim daily, claim leasing, schedule).
 *
 * Context rather than a hook because the rewards card, the claim modal, the
 * schedule modal and the lease list all read the SAME
 * `calculateUnlockedRewardsResponse` — a per-component hook would refetch it
 * four times and show four different numbers mid-claim.
 *
 * Every network call goes through `src/lib/api/claims.ts`, which parses the
 * reply with the shared schema. A non-2xx throws, so the `catch` in each flow is
 * the single place an error becomes a toast.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { logger } from "@swarm/shared/logger";
import type {
  CalculateLeasingRewardsQuery,
  CalculateLeasingRewardsResponse,
  CalculateUnlockedRewardsQuery,
  CalculateUnlockedRewardsResponse,
  ClaimDailyRewardsRequest,
  ClaimLeasingRewardsRequest,
  GetClaimsByUserAddressQuery,
  GetClaimsByUserAddressResponse,
  ScheduleClaimRequest,
} from "@swarm/shared/schemas";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import * as claimsApi from "@/lib/api/claims";
import { apiErrorMessage } from "@/lib/api/client";
import { formatClaimableAmount, TRUTH_DECIMALS } from "@/lib/currency/tokenAmount";
import { useToast } from "@/ui/shadcn/use-toast";

import { useLicenseLeasesContext } from "../license-leases/useLicenseLeasesContext";
import { ClaimsContext } from "./ClaimsContext";
import {
  ClaimDailyRewardsFormSchema,
  ClaimsContextControllerProps,
  ClaimsContextType,
} from "./ClaimsContext.types";

export const ClaimsContextController = ({ children }: ClaimsContextControllerProps) => {
  const [calculateUnlockedRewardsResponse, setCalculateUnlockedRewardsResponse] = useState<
    CalculateUnlockedRewardsResponse | undefined
  >(undefined);

  const [getClaimsByUserAddressResponse, setGetClaimsByUserAddressResponse] = useState<
    GetClaimsByUserAddressResponse | undefined
  >(undefined);

  const [isRewardsCardLoading, setIsRewardsCardLoading] = useState(false);

  const { toast } = useToast();

  const { getLeasesByOwner } = useLicenseLeasesContext();

  const claimDailyRewardsForm = useForm<z.infer<typeof ClaimDailyRewardsFormSchema>>({
    resolver: zodResolver(ClaimDailyRewardsFormSchema),
    defaultValues: {
      amount: "0",
    },
    mode: "all",
  });

  async function claimDailyRewards(args: ClaimDailyRewardsRequest) {
    try {
      setIsRewardsCardLoading(true);

      const data = await claimsApi.claimDailyRewards(args);

      if (!data.success) {
        throw new Error(data.error || "Claim was not successful");
      }

      await Promise.all([
        calculateUnlockedRewards({ user_address: args.user_address }),
        getClaimsByUserAddress({ user_address: args.user_address }),
      ]);

      toast({
        title: "Success",
        description: `Successfully requested ${formatClaimableAmount(data.total_requested, TRUTH_DECIMALS)} $TRUTH tokens! Transaction should execute within the next 5 minutes.`,
        variant: "accent",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: apiErrorMessage(error, "Failed to claim daily rewards"),
        variant: "destructive",
      });
    } finally {
      setIsRewardsCardLoading(false);
    }
  }

  async function claimLeasingRewards(args: ClaimLeasingRewardsRequest) {
    try {
      const data = await claimsApi.claimLeasingRewards(args);

      if (!data.success) {
        throw new Error(data.error || "Claim was not successful");
      }

      await Promise.all([
        calculateLeasingRewards({
          user_address: args.user_address,
          lease_id: args.lease_id,
        }),
        getClaimsByUserAddress({ user_address: args.user_address }),
        getLeasesByOwner(args.user_address),
      ]);

      toast({
        title: "Success",
        description: `Successfully requested ${formatClaimableAmount(data.total_requested, TRUTH_DECIMALS)} $TRUTH tokens! Transaction should execute within the next 5 minutes.`,
        variant: "accent",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: apiErrorMessage(error, "Failed to claim leasing rewards"),
        variant: "destructive",
      });
    }
  }

  async function calculateUnlockedRewards(args: CalculateUnlockedRewardsQuery) {
    try {
      setCalculateUnlockedRewardsResponse(await claimsApi.calculateUnlockedRewards(args));
    } catch (error) {
      toast({
        title: "Error",
        description: apiErrorMessage(error, "Failed to calculate unlocked rewards"),
        variant: "destructive",
      });
    }
  }

  async function calculateLeasingRewards(
    args: CalculateLeasingRewardsQuery,
  ): Promise<CalculateLeasingRewardsResponse | undefined> {
    try {
      return await claimsApi.calculateLeasingRewards(args);
    } catch (error) {
      toast({
        title: "Error",
        description: apiErrorMessage(error, "Failed to calculate leasing rewards"),
        variant: "destructive",
      });

      return undefined;
    }
  }

  async function getClaimsByUserAddress(args: GetClaimsByUserAddressQuery) {
    try {
      setGetClaimsByUserAddressResponse(await claimsApi.getClaimsByUserAddress(args));
    } catch (error) {
      toast({
        title: "Error",
        description: apiErrorMessage(error, "Failed to get claims by user address"),
        variant: "destructive",
      });
    }
  }

  async function scheduleClaimRewards(args: ScheduleClaimRequest) {
    try {
      setIsRewardsCardLoading(true);

      const data = await claimsApi.scheduleClaim(args);

      if (!data.success) {
        throw new Error(data.error || "Schedule was not successful");
      }

      await Promise.all([
        calculateUnlockedRewards({ user_address: args.user_address }),
        getClaimsByUserAddress({ user_address: args.user_address }),
      ]);

      const scheduledDate = data.scheduled_date
        ? new Date(data.scheduled_date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "the scheduled date";

      toast({
        title: "Success",
        // Truncated at 8 decimals rather than `toFixed(2)`: the receipt has to
        // name the amount that was actually scheduled, and rounding it up read
        // as more than the server recorded.
        description: `Successfully scheduled ${formatClaimableAmount(data.amount, TRUTH_DECIMALS)} $TRUTH tokens for ${scheduledDate}! Your claim will be processed automatically on that date.`,
        variant: "accent",
      });
    } catch (error) {
      logger.error(
        { err: error instanceof Error ? { message: error.message } : error },
        "schedule claim failed",
      );

      toast({
        title: "Error",
        description: apiErrorMessage(error, "Failed to schedule claim"),
        variant: "destructive",
      });
    } finally {
      setIsRewardsCardLoading(false);
    }
  }

  // Check if user has a scheduled claim (pending or processing with a scheduled date)
  const hasScheduledClaim =
    getClaimsByUserAddressResponse?.claims?.some(
      (claim) =>
        claim.scheduled_claim_date != null && // Checks for both null and undefined
        (claim.status === "pending" || claim.status === "processing"),
    ) || false;

  const props: ClaimsContextType = {
    claimDailyRewards,
    scheduleClaimRewards,
    claimLeasingRewards,
    calculateUnlockedRewards,
    calculateLeasingRewards,
    getClaimsByUserAddress,
    calculateUnlockedRewardsResponse,
    getClaimsByUserAddressResponse,
    isRewardsCardLoading,
    hasScheduledClaim,
    claimDailyRewardsForm,
  };

  return <ClaimsContext.Provider value={props}>{children}</ClaimsContext.Provider>;
};
